import { ActionRegistry } from '~/hooks/actions/ActionRegistry';
import { ActionResult } from '~/hooks/actions/enums';
import type { Action, ActionHandler } from '~/hooks/actions/types';
import { isRecord } from '~/services/layout/layout.ts';

type BottomSheetContent = {
  title?: string;
  itemsUrl?: string;
  items?: unknown[];
};

type OpenBottomSheetOptions = {
  header?: {
    title?: string;
    subtitle?: string;
  };
  content?: BottomSheetContent;
};

type ConfirmDialogOptions = {
  title?: string;
  message?: string;
  okButtonText?: string;
  cancelButtonText?: string;
};

type EditCollectionNameOptions = {
  collectionId?: string;
  name?: string;
};

type ShowTextInputOptions = {
  defaultValue?: string;
  headerTitle?: string;
  inputLabel?: string;
  buttonLabel?: string;
  maxLength?: number;
  actions?: Action[];
};

type PopupMenuPlaylistActionContext = {
  openPlaylistBottomSheet?: (
    options: OpenBottomSheetOptions
  ) => Promise<boolean>;
  confirmPlaylistDialog?: (options: ConfirmDialogOptions) => Promise<boolean>;
  closePlaylistBottomSheet?: () => void;
  editPlaylistCollectionName?: (
    options: Required<Pick<EditCollectionNameOptions, 'collectionId'>> & {
      name?: string;
    }
  ) => Promise<boolean>;
  promptPlaylistTextInput?: (
    options: ShowTextInputOptions
  ) => Promise<string | false>;
};

type PlaylistActionRegistration = {
  register: (type: string, handler: ActionHandler) => () => void;
  registrationKey?: object | string;
};

type PlaylistActionRegistrationState = {
  refCount: number;
  unregisterAll: () => void;
};

const registrationStateByObjectKey = new WeakMap<
  object,
  PlaylistActionRegistrationState
>();
const registrationStateByStringKey = new Map<
  string,
  PlaylistActionRegistrationState
>();

const openBottomSheetAction: ActionHandler<OpenBottomSheetOptions> = async (
  action
) => {
  const context = isRecord(action.context)
    ? (action.context as PopupMenuPlaylistActionContext)
    : undefined;

  if (!context?.openPlaylistBottomSheet) {
    return ActionResult.Error;
  }

  const opened = await context.openPlaylistBottomSheet(action.options ?? {});

  return opened ? ActionResult.Success : ActionResult.Error;
};

const editCollectionNameAction: ActionHandler<
  EditCollectionNameOptions
> = async (action) => {
  const context = isRecord(action.context)
    ? (action.context as PopupMenuPlaylistActionContext)
    : undefined;

  const collectionId = action.options?.collectionId;

  if (!collectionId || !context?.editPlaylistCollectionName) {
    return ActionResult.Error;
  }

  const edited = await context.editPlaylistCollectionName({
    collectionId,
    name: action.options?.name,
  });

  return edited ? ActionResult.Success : ActionResult.Cancel;
};

function getOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function getOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function withTextInputResult(actions: Action[], name: string): Action[] {
  return actions.map((action) => {
    if (action.type === 'showToast') {
      return { ...action, type: 'showConfirmationToast' };
    }

    if (
      (action.type !== 'sendCloudEvent' && action.type !== 'postForm') ||
      !isRecord(action.options)
    ) {
      return action;
    }

    const data = isRecord(action.options.data) ? action.options.data : {};

    return {
      ...action,
      options: {
        ...action.options,
        data: {
          ...data,
          name,
        },
      },
    };
  });
}

const showTextInputAction: ActionHandler<ShowTextInputOptions> = async (
  action
) => {
  const context = isRecord(action.context)
    ? (action.context as PopupMenuPlaylistActionContext)
    : undefined;

  if (!context?.promptPlaylistTextInput) {
    return ActionResult.Error;
  }

  const options = action.options ?? {};
  const name = await context.promptPlaylistTextInput({
    defaultValue: getOptionalString(options.defaultValue),
    headerTitle: getOptionalString(options.headerTitle),
    inputLabel: getOptionalString(options.inputLabel),
    buttonLabel: getOptionalString(options.buttonLabel),
    maxLength: getOptionalNumber(options.maxLength),
  });

  if (!name) {
    return ActionResult.Cancel;
  }

  const nestedActions = Array.isArray(options.actions) ? options.actions : [];

  if (nestedActions.length === 0) {
    return ActionResult.Success;
  }

  const executionResult = await ActionRegistry.getInstance().execute(
    addInflateDataToCloudEventActions(withTextInputResult(nestedActions, name)),
    { context: action.context as Record<string, unknown> }
  );

  return executionResult.final;
};

const closePlaylistBottomSheetAction: ActionHandler = async (action) => {
  const context = isRecord(action.context)
    ? (action.context as PopupMenuPlaylistActionContext)
    : undefined;

  if (!context?.closePlaylistBottomSheet) {
    return ActionResult.Error;
  }

  context.closePlaylistBottomSheet();

  return ActionResult.Success;
};

export function addInflateDataToCloudEventActions(actions: Action[]): Action[] {
  return actions.map((action) => {
    if (
      (action.type === 'sendCloudEvent' || action.type === 'postForm') &&
      isRecord(action.options)
    ) {
      const options = action.options as Record<string, unknown>;

      return {
        ...action,
        options: {
          ...options,
          inflateData: true,
        },
      } as Action;
    }

    return action;
  });
}

export function registerPlaylistPopupActions({
  register,
  registrationKey,
}: PlaylistActionRegistration): () => void {
  const resolvedRegistrationKey = registrationKey ?? register;
  const existingState =
    typeof resolvedRegistrationKey === 'string'
      ? registrationStateByStringKey.get(resolvedRegistrationKey)
      : registrationStateByObjectKey.get(resolvedRegistrationKey);

  if (existingState) {
    existingState.refCount += 1;

    let isDisposed = false;

    return () => {
      if (isDisposed) {
        return;
      }

      isDisposed = true;
      existingState.refCount -= 1;

      if (existingState.refCount > 0) {
        return;
      }

      existingState.unregisterAll();

      if (typeof resolvedRegistrationKey === 'string') {
        registrationStateByStringKey.delete(resolvedRegistrationKey);
        return;
      }

      registrationStateByObjectKey.delete(resolvedRegistrationKey);
    };
  }

  const unregisterOpenBottomSheet = register(
    'openBottomSheet',
    openBottomSheetAction
  );
  const unregisterEditCollectionName = register(
    'editCollectionName',
    editCollectionNameAction
  );
  const unregisterShowTextInput = register(
    'showTextInput',
    showTextInputAction
  );
  const unregisterClose = register('close', closePlaylistBottomSheetAction);
  const unregisterGoBackOrClose = register(
    'goBackOrClose',
    closePlaylistBottomSheetAction
  );
  const unregisterDismiss = register('dismiss', closePlaylistBottomSheetAction);

  const createdState: PlaylistActionRegistrationState = {
    refCount: 1,
    unregisterAll: () => {
      unregisterOpenBottomSheet();
      unregisterEditCollectionName();
      unregisterShowTextInput();
      unregisterClose();
      unregisterGoBackOrClose();
      unregisterDismiss();
    },
  };

  if (typeof resolvedRegistrationKey === 'string') {
    registrationStateByStringKey.set(resolvedRegistrationKey, createdState);
  } else {
    registrationStateByObjectKey.set(resolvedRegistrationKey, createdState);
  }

  let isDisposed = false;

  return () => {
    if (isDisposed) {
      return;
    }

    isDisposed = true;
    createdState.refCount -= 1;

    if (createdState.refCount > 0) {
      return;
    }

    createdState.unregisterAll();

    if (typeof resolvedRegistrationKey === 'string') {
      registrationStateByStringKey.delete(resolvedRegistrationKey);
      return;
    }

    registrationStateByObjectKey.delete(resolvedRegistrationKey);
  };
}
